import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, Star, Heart, Share2, MapPin, Users, Bed, Bath, 
  Check, X, Calendar, ChevronLeft, ChevronRight, Loader2,
  Home, Wifi, Car, Waves, Wind, Coffee, Tv, UtensilsCrossed,
  Shield, Clock, CreditCard, Smartphone, Globe
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Amenity icons mapping
const amenityIcons = {
  'WiFi': Wifi,
  'Parking': Car,
  'Pool': Waves,
  'Air Conditioning': Wind,
  'Kitchen': UtensilsCrossed,
  'TV': Tv,
  'Coffee Maker': Coffee,
};

// Image Gallery Component
const ImageGallery = ({ photos, propertyName }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const images = photos?.length > 0 
    ? photos.map(p => p.url) 
    : ['https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&auto=format&fit=crop&q=60'];
  
  return (
    <div className="relative">
      {/* Main Image */}
      <div className="aspect-[16/9] md:aspect-[21/9] rounded-2xl overflow-hidden bg-gray-100">
        <img 
          src={images[selectedIndex]} 
          alt={propertyName}
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Navigation */}
      {images.length > 1 && (
        <>
          <button
            onClick={() => setSelectedIndex(i => i === 0 ? images.length - 1 : i - 1)}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setSelectedIndex(i => i === images.length - 1 ? 0 : i + 1)}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          
          {/* Thumbnails */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedIndex(idx)}
                className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 ${
                  idx === selectedIndex ? 'border-[#FF385C]' : 'border-transparent'
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Booking Card Component with Date Range Picker
const BookingCard = ({ property, onBook, initialDates }) => {
  // Parse initial dates from URL params
  const parseInitialDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };
  
  const [dateRange, setDateRange] = useState({ 
    start: parseInitialDate(initialDates?.checkIn), 
    end: parseInitialDate(initialDates?.checkOut) 
  });
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [guests, setGuests] = useState(parseInt(initialDates?.guests) || 2);
  const [calculating, setCalculating] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [error, setError] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 640;
    }
    return false;
  });
  
  // Auto-calculate price if initial dates were provided
  useEffect(() => {
    if (initialDates?.checkIn && initialDates?.checkOut && property) {
      // Trigger price calculation for pre-filled dates
      const start = parseInitialDate(initialDates.checkIn);
      const end = parseInitialDate(initialDates.checkOut);
      if (start && end && dateRange.start && dateRange.end) {
        // calculatePrice will be triggered by the dateRange.end effect
      }
    }
  }, [property]);
  
  // Check for mobile on resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };
  
  // Format date for display
  const formatDisplayDate = (date) => {
    if (!date) return 'Add date';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  // Get first day of month (0 = Sunday)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };
  
  // Check if date is in the past
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  
  // Check if date is selected
  const isSelected = (date) => {
    if (!dateRange.start) return false;
    return date.getTime() === dateRange.start.getTime() || 
           (dateRange.end && date.getTime() === dateRange.end.getTime());
  };
  
  // Check if date is in range
  const isInRange = (date) => {
    if (!dateRange.start || !dateRange.end) return false;
    return date > dateRange.start && date < dateRange.end;
  };
  
  // Handle date click
  const handleDateClick = (date) => {
    if (isPastDate(date)) return;
    
    if (!dateRange.start || (dateRange.start && dateRange.end)) {
      // Start new selection
      setDateRange({ start: date, end: null });
      setSelectingEnd(true);
    } else if (selectingEnd) {
      // Select end date
      if (date > dateRange.start) {
        setDateRange({ ...dateRange, end: date });
        setSelectingEnd(false);
        setShowCalendar(false);
      } else {
        // If clicked date is before start, make it the new start
        setDateRange({ start: date, end: null });
      }
    }
  };
  
  // Render calendar month
  const renderMonth = (monthOffset = 0) => {
    const month = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1);
    const daysInMonth = getDaysInMonth(month.getFullYear(), month.getMonth());
    const firstDay = getFirstDayOfMonth(month.getFullYear(), month.getMonth());
    const days = [];
    
    // Empty cells for days before first of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${monthOffset}-${i}`} className="aspect-square" />);
    }
    
    // Day cells
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(month.getFullYear(), month.getMonth(), day);
      const isPast = isPastDate(date);
      const selected = isSelected(date);
      const inRange = isInRange(date);
      const isStart = dateRange.start && date.getTime() === dateRange.start.getTime();
      const isEnd = dateRange.end && date.getTime() === dateRange.end.getTime();
      
      days.push(
        <button
          key={`${monthOffset}-${day}`}
          onClick={() => handleDateClick(date)}
          disabled={isPast}
          className={`aspect-square rounded-full text-sm font-medium transition-all flex items-center justify-center
            ${isPast ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 active:scale-95'}
            ${selected ? 'bg-gray-900 text-white hover:bg-gray-800' : ''}
            ${inRange ? 'bg-gray-100' : ''}
            ${isStart && dateRange.end ? 'rounded-r-none' : ''}
            ${isEnd ? 'rounded-l-none' : ''}
          `}
        >
          {day}
        </button>
      );
    }
    
    return (
      <div className="flex-1 min-w-0">
        <div className="text-center font-semibold text-gray-900 mb-3 text-sm sm:text-base">
          {month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center text-xs text-gray-500 mb-2">
          <div>Su</div><div>Mo</div><div>Tu</div><div>We</div><div>Th</div><div>Fr</div><div>Sa</div>
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {days}
        </div>
      </div>
    );
  };
  
  const calculatePrice = useCallback(async () => {
    if (!dateRange.start || !dateRange.end) return;
    
    setCalculating(true);
    setError(null);
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/marketplace/bookings/calculate?property_id=${property.property_id}&check_in=${formatDate(dateRange.start)}&check_out=${formatDate(dateRange.end)}&guests=${guests}`,
        { method: 'POST' }
      );
      
      // Handle 409 Conflict (unavailable dates) BEFORE parsing JSON
      if (resp.status === 409) {
        setError('Property not available for these dates. Please select different dates.');
        setPricing(null);
        return;
      }
      
      // Try to parse response
      let data;
      try {
        data = await resp.json();
      } catch (parseErr) {
        setError('Failed to check availability');
        setPricing(null);
        return;
      }
      
      if (!resp.ok) {
        setError(data.detail || 'Failed to calculate price');
        setPricing(null);
        return;
      }
      
      setPricing(data);
    } catch (err) {
      console.error('Price calculation error:', err);
      setError('Unable to check availability. Please try again.');
      setPricing(null);
    } finally {
      setCalculating(false);
    }
  }, [property.property_id, dateRange.start, dateRange.end, guests]);
  
  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      calculatePrice();
    }
  }, [dateRange.start, dateRange.end, guests, calculatePrice]);
  
  const handleReserve = () => {
    if (!pricing) return;
    onBook({ 
      checkIn: formatDate(dateRange.start), 
      checkOut: formatDate(dateRange.end), 
      guests, 
      pricing 
    });
  };
  
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 sticky top-24">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <span className="text-2xl font-bold text-gray-900">
            {property.currency || 'KES'} {property.base_price?.toLocaleString()}
          </span>
          <span className="text-gray-500"> / night</span>
        </div>
        {property.average_rating > 0 && (
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="font-medium">{property.average_rating?.toFixed(1)}</span>
            {property.review_count > 0 && (
              <span className="text-gray-500">({property.review_count})</span>
            )}
          </div>
        )}
      </div>
      
      {/* Date Range Picker */}
      <div className="relative mb-4">
        <div 
          className="border border-gray-300 rounded-xl overflow-hidden cursor-pointer hover:border-gray-400 transition-colors"
          onClick={() => setShowCalendar(true)}
        >
          <div className="grid grid-cols-2 divide-x divide-gray-300">
            <div className="p-3">
              <div className="text-xs font-semibold text-gray-800">CHECK-IN</div>
              <div className="text-sm mt-1" data-testid="booking-checkin-display">
                {formatDisplayDate(dateRange.start)}
              </div>
            </div>
            <div className="p-3">
              <div className="text-xs font-semibold text-gray-800">CHECKOUT</div>
              <div className="text-sm mt-1" data-testid="booking-checkout-display">
                {formatDisplayDate(dateRange.end)}
              </div>
            </div>
          </div>
        </div>
        
        {/* Calendar Dropdown - Responsive */}
        {showCalendar && (
          <>
            {/* Mobile: Full-screen modal with single month */}
            <div className="sm:hidden fixed inset-0 z-50 bg-white">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <button 
                    onClick={() => setShowCalendar(false)}
                    className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <span className="font-semibold text-gray-900">
                    {selectingEnd ? 'Select checkout' : 'Select check-in'}
                  </span>
                  <div className="w-9" /> {/* Spacer */}
                </div>
                
                {/* Selected dates display */}
                <div className="flex border-b border-gray-200">
                  <div className={`flex-1 p-4 text-center ${!selectingEnd ? 'bg-gray-50' : ''}`}>
                    <div className="text-xs text-gray-500 uppercase">Check-in</div>
                    <div className="font-semibold mt-1">{formatDisplayDate(dateRange.start)}</div>
                  </div>
                  <div className={`flex-1 p-4 text-center border-l border-gray-200 ${selectingEnd ? 'bg-gray-50' : ''}`}>
                    <div className="text-xs text-gray-500 uppercase">Checkout</div>
                    <div className="font-semibold mt-1">{formatDisplayDate(dateRange.end)}</div>
                  </div>
                </div>
                
                {/* Month navigation */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <button 
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="font-semibold text-gray-900">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                  <button 
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Single month calendar */}
                <div className="flex-1 overflow-y-auto p-4">
                  {renderMonth(0)}
                </div>
                
                {/* Footer */}
                <div className="p-4 border-t border-gray-200 flex gap-3 safe-area-inset-bottom">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setDateRange({ start: null, end: null });
                      setSelectingEnd(false);
                      setPricing(null);
                    }}
                    className="flex-1 rounded-xl h-12"
                  >
                    Clear
                  </Button>
                  <Button 
                    onClick={() => setShowCalendar(false)}
                    disabled={!dateRange.start}
                    className="flex-1 bg-gray-900 hover:bg-gray-800 text-white rounded-xl h-12"
                  >
                    {dateRange.start && dateRange.end 
                      ? `${Math.ceil((dateRange.end - dateRange.start) / (1000 * 60 * 60 * 24))} nights` 
                      : 'Select dates'}
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Desktop: Dropdown with two months */}
            <div className="hidden sm:block">
              <div className="fixed inset-0 z-40" onClick={() => setShowCalendar(false)} />
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl p-6 z-50" data-testid="date-range-calendar">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-4">
                  <button 
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm font-medium text-gray-600">
                    {selectingEnd ? 'Select checkout date' : 'Select check-in date'}
                  </span>
                  <button 
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Two Month View */}
                <div className="flex gap-8">
                  {renderMonth(0)}
                  {renderMonth(1)}
                </div>
                
                {/* Clear Dates Button */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between">
                  <button 
                    onClick={() => {
                      setDateRange({ start: null, end: null });
                      setSelectingEnd(false);
                      setPricing(null);
                    }}
                    className="text-sm font-medium text-gray-700 hover:underline"
                  >
                    Clear dates
                  </button>
                  <button 
                    onClick={() => setShowCalendar(false)}
                    className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Guests Selector */}
      <div className="border border-gray-300 rounded-xl p-3 mb-4">
        <div className="text-xs font-semibold text-gray-800">GUESTS</div>
        <select
          value={guests}
          onChange={(e) => setGuests(parseInt(e.target.value))}
          className="w-full text-sm bg-transparent border-0 focus:ring-0 mt-1"
          data-testid="booking-guests"
        >
          {[...Array(property.max_guests || 4)].map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1} guest{i > 0 ? 's' : ''}
            </option>
          ))}
        </select>
      </div>
      
      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}
      
      {/* Pricing Breakdown */}
      {calculating && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      )}
      
      {pricing && !calculating && (
        <div className="space-y-3 mb-6 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600 underline">
              {property.currency || 'KES'} {property.base_price?.toLocaleString()} × {pricing.nights} nights
            </span>
            <span>{pricing.currency} {pricing.breakdown.accommodation.toLocaleString()}</span>
          </div>
          {pricing.breakdown.cleaning_fee > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-600">Cleaning fee</span>
              <span>{pricing.currency} {pricing.breakdown.cleaning_fee.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">Service fee</span>
            <span>{pricing.currency} {pricing.breakdown.service_fee.toLocaleString()}</span>
          </div>
          <div className="flex justify-between pt-3 border-t border-gray-200 font-semibold">
            <span>Total</span>
            <span>{pricing.currency} {pricing.breakdown.total.toLocaleString()}</span>
          </div>
        </div>
      )}
      
      {/* Reserve Button */}
      <Button 
        onClick={handleReserve}
        disabled={!pricing || calculating}
        className="w-full bg-gradient-to-r from-[#FF385C] to-[#E31C5F] hover:from-[#E31C5F] hover:to-[#C91848] text-white rounded-xl h-12 text-base font-semibold"
        data-testid="reserve-btn"
      >
        {calculating ? 'Calculating...' : 'Reserve'}
      </Button>
      
      <p className="text-center text-sm text-gray-500 mt-3">
        You won't be charged yet
      </p>
    </div>
  );
};

// Main Property Detail Page
export default function MarketplacePropertyPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [property, setProperty] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [reviews, setReviews] = useState({ reviews: [], total: 0, average_rating: 0 });
  
  // Get initial dates from URL params
  const initialDates = {
    checkIn: searchParams.get('checkIn'),
    checkOut: searchParams.get('checkOut'),
    guests: searchParams.get('guests')
  };
  
  // Load property and reviews
  useEffect(() => {
    const loadProperty = async () => {
      try {
        // Load settings first
        const settingsResp = await fetch(`${BACKEND_URL}/api/marketplace/settings`);
        if (settingsResp.ok) {
          setSettings(await settingsResp.json());
        }
        
        // Load property
        const resp = await fetch(`${BACKEND_URL}/api/marketplace/properties/${propertyId}`);
        if (!resp.ok) {
          if (resp.status === 404) throw new Error('Property not found');
          throw new Error('Failed to load property');
        }
        
        const data = await resp.json();
        setProperty(data);
        
        // Load reviews
        try {
          const reviewsResp = await fetch(`${BACKEND_URL}/api/reviews/public/${propertyId}`);
          if (reviewsResp.ok) {
            setReviews(await reviewsResp.json());
          }
        } catch (err) {
          console.error('Failed to load reviews:', err);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadProperty();
  }, [propertyId]);
  
  const handleBook = (data) => {
    setBookingData(data);
    setShowBookingForm(true);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF385C]" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{error}</h1>
          <Button onClick={() => navigate('/marketplace')} variant="outline">
            Back to iStayBnB
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white" data-testid="property-detail-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/marketplace')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/marketplace')}>
              <div className="w-8 h-8 bg-gradient-to-br from-[#FF385C] to-[#E31C5F] rounded-lg flex items-center justify-center">
                <Home className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-gray-900">{settings?.name || 'iStayBnB'}</span>
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" className="gap-2">
                <Share2 className="w-4 h-4" /> Share
              </Button>
              <Button variant="ghost" size="sm" className="gap-2">
                <Heart className="w-4 h-4" /> Save
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Image Gallery */}
        <ImageGallery photos={property.photos} propertyName={property.name} />
        
        {/* Property Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mt-8">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title & Quick Info */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{property.name}</h1>
              <div className="flex flex-wrap items-center gap-4 text-gray-600">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" /> {property.max_guests} guests
                </span>
                <span className="flex items-center gap-1">
                  <Bed className="w-4 h-4" /> {property.bedrooms} bedroom{property.bedrooms !== 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1">
                  <Bath className="w-4 h-4" /> {property.bathrooms} bath{property.bathrooms !== 1 ? 's' : ''}
                </span>
              </div>
              <p className="flex items-center gap-1 text-gray-500 mt-2">
                <MapPin className="w-4 h-4" />
                {property.address && `${property.address}, `}{property.city}{property.country ? `, ${property.country}` : ''}
              </p>
            </div>
            
            {/* Host Info */}
            {property.host && (
              <div className="flex items-center gap-4 py-6 border-y border-gray-200">
                <div className="w-14 h-14 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-xl font-semibold text-gray-600">
                    {property.host.name?.charAt(0) || 'H'}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Hosted by {property.host.name}</p>
                  {property.host.member_since && (
                    <p className="text-gray-500 text-sm">Member since {property.host.member_since}</p>
                  )}
                </div>
              </div>
            )}
            
            {/* Description */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">About this place</h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                {property.description || 'No description available.'}
              </p>
            </div>
            
            {/* Amenities */}
            {property.amenities?.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">What this place offers</h2>
                <div className="grid grid-cols-2 gap-4">
                  {property.amenities.slice(0, 10).map((amenity, idx) => {
                    const Icon = amenityIcons[amenity] || Check;
                    return (
                      <div key={idx} className="flex items-center gap-3 text-gray-700">
                        <Icon className="w-5 h-5 text-gray-500" />
                        <span>{amenity}</span>
                      </div>
                    );
                  })}
                </div>
                {property.amenities.length > 10 && (
                  <Button variant="outline" className="mt-4">
                    Show all {property.amenities.length} amenities
                  </Button>
                )}
              </div>
            )}
            
            {/* Reviews */}
            {reviews.total > 0 && (
              <div data-testid="reviews-section">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                  {reviews.average_rating?.toFixed(1)} · {reviews.total} review{reviews.total !== 1 ? 's' : ''}
                </h2>
                <div className="space-y-6">
                  {reviews.reviews.slice(0, 5).map((review, idx) => (
                    <div key={review.review_id || idx} className="pb-6 border-b border-gray-100 last:border-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <span className="font-medium text-gray-600">
                            {review.guest_name?.charAt(0) || 'G'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{review.guest_name}</p>
                          <p className="text-gray-500 text-sm">{review.created_at?.slice(0, 10)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-4 h-4 ${i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} 
                          />
                        ))}
                      </div>
                      {review.title && (
                        <p className="font-medium text-gray-800 mb-1">{review.title}</p>
                      )}
                      <p className="text-gray-600">{review.content}</p>
                      
                      {/* Host Response */}
                      {review.host_response && (
                        <div className="mt-3 bg-gray-50 rounded-lg p-4 border-l-4 border-[#FF385C]">
                          <p className="text-sm font-medium text-gray-700 mb-1">Response from host:</p>
                          <p className="text-sm text-gray-600">{review.host_response}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {reviews.total > 5 && (
                  <Button variant="outline" className="mt-4">
                    Show all {reviews.total} reviews
                  </Button>
                )}
              </div>
            )}
            
            {/* House Rules */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Things to know</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">House rules</h3>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li className="flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Check-in: 2:00 PM - 10:00 PM
                    </li>
                    <li className="flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Checkout before 11:00 AM
                    </li>
                    <li className="flex items-center gap-2">
                      <Users className="w-4 h-4" /> {property.max_guests} guests maximum
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Safety & property</h3>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li className="flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Security camera/recording device
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4" /> Smoke alarm
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Cancellation policy</h3>
                  <p className="text-gray-600 text-sm">
                    Free cancellation before check-in. Review the full policy.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <BookingCard property={property} onBook={handleBook} initialDates={initialDates} />
          </div>
        </div>
      </main>
      
      {/* Booking Form Modal */}
      {showBookingForm && bookingData && (
        <BookingFormModal 
          property={property}
          bookingData={bookingData}
          onClose={() => setShowBookingForm(false)}
          settings={settings}
        />
      )}
    </div>
  );
}

// Booking Form Modal Component
function BookingFormModal({ property, bookingData, onClose, settings }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    special_requests: '',
    payment_method: 'stripe'
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      const resp = await fetch(`${BACKEND_URL}/api/marketplace/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: property.property_id,
          check_in: bookingData.checkIn,
          check_out: bookingData.checkOut,
          guests: bookingData.guests,
          ...formData
        })
      });
      
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.detail || 'Failed to create booking');
      }
      
      const result = await resp.json();
      
      // Redirect to payment or confirmation
      navigate(`/marketplace/booking/${result.booking_id}/confirm`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Complete your booking</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Booking Summary */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex gap-4">
              <img 
                src={property.photos?.[0]?.url || property.image_url || 'https://via.placeholder.com/100'} 
                alt={property.name}
                className="w-20 h-20 rounded-lg object-cover"
              />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{property.name}</h3>
                <p className="text-sm text-gray-500">{property.city}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {bookingData.checkIn} → {bookingData.checkOut} · {bookingData.guests} guest{bookingData.guests > 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between font-semibold">
              <span>Total</span>
              <span>{bookingData.pricing.currency} {bookingData.pricing.breakdown.total.toLocaleString()}</span>
            </div>
          </div>
          
          {/* Guest Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Your details</h3>
            <div>
              <Label>Full name</Label>
              <Input
                required
                value={formData.guest_name}
                onChange={(e) => setFormData(f => ({ ...f, guest_name: e.target.value }))}
                placeholder="John Smith"
                className="mt-1"
                data-testid="guest-name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                required
                value={formData.guest_email}
                onChange={(e) => setFormData(f => ({ ...f, guest_email: e.target.value }))}
                placeholder="john@example.com"
                className="mt-1"
                data-testid="guest-email"
              />
            </div>
            <div>
              <Label>Phone number</Label>
              <Input
                type="tel"
                required
                value={formData.guest_phone}
                onChange={(e) => setFormData(f => ({ ...f, guest_phone: e.target.value }))}
                placeholder="+254 700 000000"
                className="mt-1"
                data-testid="guest-phone"
              />
            </div>
            <div>
              <Label>Special requests (optional)</Label>
              <Textarea
                value={formData.special_requests}
                onChange={(e) => setFormData(f => ({ ...f, special_requests: e.target.value }))}
                placeholder="Any special requirements..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          
          {/* Payment Method */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Payment method</h3>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setFormData(f => ({ ...f, payment_method: 'stripe' }))}
                className={`p-4 rounded-xl border-2 text-center transition-colors ${
                  formData.payment_method === 'stripe' 
                    ? 'border-[#FF385C] bg-pink-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <CreditCard className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                <span className="text-sm font-medium">Card</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData(f => ({ ...f, payment_method: 'mpesa' }))}
                className={`p-4 rounded-xl border-2 text-center transition-colors ${
                  formData.payment_method === 'mpesa' 
                    ? 'border-[#FF385C] bg-pink-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Smartphone className="w-6 h-6 mx-auto mb-2 text-green-600" />
                <span className="text-sm font-medium">M-Pesa</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData(f => ({ ...f, payment_method: 'pesapal' }))}
                className={`p-4 rounded-xl border-2 text-center transition-colors ${
                  formData.payment_method === 'pesapal' 
                    ? 'border-[#FF385C] bg-pink-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Globe className="w-6 h-6 mx-auto mb-2 text-orange-600" />
                <span className="text-sm font-medium">Pesapal</span>
              </button>
            </div>
          </div>
          
          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          {/* Submit */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-[#FF385C] to-[#E31C5F] hover:from-[#E31C5F] hover:to-[#C91848] text-white rounded-xl h-12 text-base font-semibold"
            data-testid="confirm-booking-btn"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Processing...</>
            ) : (
              'Confirm and pay'
            )}
          </Button>
          
          <p className="text-xs text-gray-500 text-center">
            By selecting the button, you agree to the House Rules, Ground rules for guests, 
            and the {settings?.name || 'iStayBnB'} Terms of Service.
          </p>
        </form>
      </div>
    </div>
  );
}
